import is from '@sindresorhus/is';
import { AnimatePresence, motion } from 'framer-motion';
import noop from 'lodash/noop';
import type { FunctionComponent, ReactNode } from 'react';
import { Fragment, useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobRole } from '~/shared/data-contract/job-role';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';
import type { Translate } from '~/shared/internationalization/translation';
import { Badge, BadgeButton } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~/ui/core/dropdown-menu/DropdownMenu';
import { CheckIcon } from '~/ui/core/icons';

import { createAssignReviewerItem, useAssignReviewer } from '../api/use-assign-reviewer';
import { useAssignableReviewers } from '../api/use-assignable-reviewers';
import { useReviewJobItemVersion } from '../api/use-review-job-item-version';
import { useUnassignReviewer } from '../api/use-unassign-reviewer';
import { useTrackWorkflowTransition } from '../hooks/analytics/use-track-workflow-transition';
import { useConfirmDialogPreference } from '../hooks/use-confirm-dialog-preference';
import { useConfirm } from '../providers/ConfirmProvider';
import { ReviewerContextMenu, ReviewerContextMenuItems } from './AssignReviewerDropdown';
import { FormattedName } from './FormattedName';

type JobItem = JobLevel | JobLevelDetails | JobRole | JobRoleDetails;

type ItemType = 'job-level' | 'job-role';

type GenericReviewerContextMenuProps = {
    readonly item: JobItem;
    readonly itemType: ItemType;
    readonly jobArchitectureId: string;
    readonly jobClusterId?: string;
    readonly onInvite?: () => void;
    readonly onlyAssignReviewer?: boolean;
};

type GenericReviewerContextMenuItemsProps = {
    readonly items: readonly JobItem[];
    readonly itemType: ItemType;
    readonly jobArchitectureId: string;
    readonly jobClusterId?: string;
};

type ReviewerOption = {
    readonly id: string;
    readonly firstName: string;
    readonly lastName: string;
};

type ReviewerMenuPropsReturn = {
    readonly assignableReviewers: readonly ReviewerOption[] | undefined;
    readonly currentStatus: 'approved' | 'assigned';
    readonly handleReviewerSelect: (reviewerId: string, firstName: string, lastName: string) => Promise<void>;
    readonly handleStatusChange?: (status: 'approved' | 'assigned') => Promise<void>;
    readonly handleUnassignReviewer: () => void;
    readonly selectedReviewer: JobItem['reviewer'];
    readonly setSearchTerm: (term: string) => void;
};

const getParentRoleId = (item: JobItem, itemType: 'job-level' | 'job-role'): string | undefined =>
    itemType === 'job-level' && !is.null(item.parentId) ? item.parentId : undefined;

const getStatusTransitionParams = (
    action: 'review' | 'unreview',
): { readonly actionType: 'review_completed' | 'unreview'; readonly fromStatus: string; readonly toStatus: string } =>
    action === 'review'
        ? { actionType: 'review_completed', fromStatus: 'in_review', toStatus: 'reviewed' }
        : { actionType: 'unreview', fromStatus: 'reviewed', toStatus: 'in_review' };

const getItemTypeTranslation = (itemType: ItemType, translate: Translate): string => {
    switch (itemType) {
        case 'job-role':
            return translate('pages.job-architecture-v2.job-role');
        case 'job-level':
            return translate('pages.job-architecture-v2.job-level');
    }
};

const getVersionId = (item: JobItem): string => item.versionInfo?.version.toString() ?? '1';

const useReviewerMenuProps = ({
    item,
    itemType,
    jobArchitectureId,
    jobClusterId,
    onlyAssignReviewer = false,
}: GenericReviewerContextMenuProps): ReviewerMenuPropsReturn => {
    const [searchTerm, setSearchTerm] = useState('');
    const { data: assignableReviewers } = useAssignableReviewers({
        jobRoleOrJobLevel: {
            id: item.id,
            architectureId: jobArchitectureId,
            type: itemType === 'job-role' ? 'role' : 'level',
        },
        searchTerm,
    });
    const { translate } = useContext(ApplicationContext);
    const { mutateAsync: assignReviewer } = useAssignReviewer();
    const { mutate: unassignReviewer } = useUnassignReviewer();
    const { mutateAsync: reviewJobItemVersion } = useReviewJobItemVersion();
    const { confirm } = useConfirm();
    const { trackWorkflowTransition } = useTrackWorkflowTransition({ architectureId: jobArchitectureId });
    const { dismissDialog, shouldShowDialog } = useConfirmDialogPreference('review');
    const itemTypeTranslation = getItemTypeTranslation(itemType, translate);

    const renderCancelButton = (onClick: () => void): ReactNode => {
        return (
            <Button variant="tertiary" onClick={onClick}>
                {translate('common.cancel')}
            </Button>
        );
    };

    const renderConfirmButton = (onClick: () => void): ReactNode => {
        return (
            <Button variant="primary" onClick={onClick}>
                {translate('pages.job-architecture.mark-as-reviewed')}
            </Button>
        );
    };

    const handleUnassignReviewer = (): void => {
        unassignReviewer(item);
    };

    const handleReviewerSelect = async (reviewerId: string, firstName: string, lastName: string): Promise<void> => {
        if (is.undefined(jobClusterId)) {
            return;
        }

        await assignReviewer({
            clusterId: jobClusterId,
            item: createAssignReviewerItem(item, { id: reviewerId, firstName, lastName, state: 'assigned' }, itemType),
            updateType: item.reviewer === undefined ? 'create' : 'update',
        });

        trackWorkflowTransition({
            actionType: 'assign_reviewer',
            itemId: item.id,
            itemType,
            parentRoleId: getParentRoleId(item, itemType),
            targetUserId: reviewerId,
            toStatus: 'in_review',
            versionId: getVersionId(item),
        });
    };

    const handleStatusChange = async (status: 'approved' | 'assigned', jobTypeTranslation: string): Promise<void> => {
        if (status === 'approved' && shouldShowDialog) {
            const result = await confirm({
                description: translate('pages.job-architecture-v2.mark-job-role-reviewed-content', {
                    jobType: jobTypeTranslation,
                }),
                renderCancelButton,
                renderConfirmButton,
                showDontShowAgainCheckbox: true,
                title: translate('pages.job-architecture-v2.mark-job-role-reviewed-title', {
                    jobType: jobTypeTranslation,
                }),
                type: 'default',
            });

            if (result.dontShowAgain) {
                dismissDialog();
            }
        }

        if (is.undefined(jobClusterId)) {
            return;
        }

        const action = status === 'approved' ? 'review' : 'unreview';
        const versionId = getVersionId(item);

        await reviewJobItemVersion({
            action,
            architectureId: jobArchitectureId,
            itemId: item.id,
            itemType,
            version: versionId,
        });

        const transitionParams = getStatusTransitionParams(action);
        trackWorkflowTransition({
            ...transitionParams,
            itemId: item.id,
            itemType,
            parentRoleId: getParentRoleId(item, itemType),
            versionId,
        });
    };

    const currentStatus: 'approved' | 'assigned' = item.versionInfo?.isReviewed === true ? 'approved' : 'assigned';

    return {
        assignableReviewers,
        currentStatus,
        handleReviewerSelect,
        handleStatusChange: onlyAssignReviewer
            ? undefined
            : (status) => handleStatusChange(status, itemTypeTranslation),
        handleUnassignReviewer,
        selectedReviewer: item.reviewer,
        setSearchTerm,
    };
};

type MultiReviewerMenuPropsReturn = {
    readonly assignableReviewers: readonly ReviewerOption[] | undefined;
    readonly handleReviewerSelect: (reviewerId: string, firstName: string, lastName: string) => Promise<void>;
    readonly setSearchTerm: (term: string) => void;
};

const useMultiReviewerMenuProps = ({
    items,
    itemType,
    jobArchitectureId,
    jobClusterId,
}: GenericReviewerContextMenuItemsProps): MultiReviewerMenuPropsReturn => {
    const [searchTerm, setSearchTerm] = useState('');
    const firstItem = items[0];
    const { data: assignableReviewers } = useAssignableReviewers({
        jobRoleOrJobLevel: {
            id: firstItem?.id ?? '',
            architectureId: jobArchitectureId,
            type: itemType === 'job-role' ? 'role' : 'level',
        },
        searchTerm,
    });
    const { translate } = useContext(ApplicationContext);
    const { mutateAsync: assignReviewer } = useAssignReviewer();
    const { confirm } = useConfirm();
    const { trackWorkflowTransition } = useTrackWorkflowTransition({ architectureId: jobArchitectureId });

    const assignReviewerToItem = async (
        item: JobItem,
        reviewerId: string,
        firstName: string,
        lastName: string,
        clusterId: string,
    ): Promise<void> => {
        await assignReviewer({
            clusterId,
            item: createAssignReviewerItem(item, { id: reviewerId, firstName, lastName, state: 'assigned' }, itemType),
            updateType: item.reviewer === undefined ? 'create' : 'update',
        });

        trackWorkflowTransition({
            actionType: 'assign_reviewer',
            itemId: item.id,
            itemType,
            parentRoleId: getParentRoleId(item, itemType),
            targetUserId: reviewerId,
            toStatus: 'in_review',
            versionId: getVersionId(item),
        });
    };

    const handleReviewerSelect = async (reviewerId: string, firstName: string, lastName: string): Promise<void> => {
        if (is.undefined(jobClusterId) || is.emptyArray(items)) {
            return;
        }

        if (items.length > 1) {
            await confirm({
                description: (
                    <Fragment>
                        {translate('pages.job-architecture-v2.assign-reviewer-multi-description')}
                        <ul>
                            {items.map((item) => (
                                <li key={item.id}>{item.title}</li>
                            ))}
                        </ul>
                    </Fragment>
                ),
                title: translate('pages.job-architecture-v2.assign-reviewer-multi-title'),
                type: 'default',
            });
        }

        for (const item of items) {
            assignReviewerToItem(item, reviewerId, firstName, lastName, jobClusterId);
        }
    };

    return {
        assignableReviewers,
        handleReviewerSelect,
        setSearchTerm,
    };
};

export const GenericReviewerContextMenuComponent: FunctionComponent<GenericReviewerContextMenuProps> = (props) => {
    const {
        assignableReviewers,
        currentStatus,
        handleReviewerSelect,
        handleStatusChange,
        handleUnassignReviewer,
        selectedReviewer,
        setSearchTerm,
    } = useReviewerMenuProps(props);

    return (
        <ReviewerContextMenu
            currentStatus={currentStatus}
            reviewers={assignableReviewers ?? []}
            selectedReviewer={selectedReviewer}
            onInvite={props.onInvite}
            onSearch={setSearchTerm}
            onSelectReviewer={({ id, firstName, lastName }) => handleReviewerSelect(id, firstName, lastName)}
            onStatusChange={handleStatusChange}
            onUnassignReviewer={handleUnassignReviewer}
        />
    );
};

export const GenericReviewerContextMenuItemsForContextMenu: FunctionComponent<GenericReviewerContextMenuItemsProps> = (
    props,
) => {
    const { assignableReviewers, handleReviewerSelect, setSearchTerm } = useMultiReviewerMenuProps(props);

    return (
        <ReviewerContextMenuItems
            currentStatus="assigned"
            reviewers={assignableReviewers ?? []}
            selectedReviewer={undefined}
            onSearch={setSearchTerm}
            onSelectReviewer={({ id, firstName, lastName }) => handleReviewerSelect(id, firstName, lastName)}
            onStatusChange={undefined}
            onUnassignReviewer={noop}
        />
    );
};

const BadgeContent: FunctionComponent<{
    readonly defaultText: string;
    readonly reviewer: JobItem['reviewer'];
    readonly showAnimatedIcon: boolean;
}> = ({ defaultText, reviewer, showAnimatedIcon }) => {
    if (!showAnimatedIcon) {
        return <FormattedName defaultText={defaultText} reviewer={reviewer} />;
    }

    return (
        <AnimatePresence mode="sync" propagate={true}>
            <motion.div
                key="animated-icon"
                animate={{ opacity: 1, scale: 1 }}
                exit={{ translateY: '0%' }}
                initial={{
                    opacity: 0,
                    position: 'absolute',
                    translateY: '-150%',
                }}
                transition={{ duration: 0.2 }}
            >
                <CheckIcon height={16} width={16} />
            </motion.div>
            <motion.div
                key="formatted-name"
                animate={{ opacity: 1, scale: 1 }}
                exit={{ translateY: '150%' }}
                initial={false}
                transition={{ duration: 0.2 }}
            >
                <FormattedName defaultText={defaultText} reviewer={reviewer} />
            </motion.div>
        </AnimatePresence>
    );
};

type ReviewerBadgeChromeVariant = 'gray' | 'warning';

const ReviewerBadgeDropdown: FunctionComponent<{
    readonly badgeClassName?: string;
    readonly chromeVariant?: ReviewerBadgeChromeVariant;
    readonly defaultText: string;
    readonly disabled?: boolean;
    readonly item: JobItem;
    readonly itemType: 'job-level' | 'job-role';
    readonly jobArchitectureId: string;
    readonly jobClusterId?: string;
    readonly onInvite?: () => void;
    readonly onlyAssignReviewer?: boolean;
    readonly showAnimatedIcon: boolean;
}> = ({
    badgeClassName,
    chromeVariant = 'warning',
    defaultText,
    disabled = false,
    item,
    itemType,
    jobArchitectureId,
    jobClusterId,
    onInvite,
    onlyAssignReviewer = false,
    showAnimatedIcon,
}) => {
    const isDisabled = disabled || !item.actions?.canAssignReviewer;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger disabled={isDisabled}>
                <BadgeButton>
                    <Badge
                        className={badgeClassName}
                        data-testid="assign-reviewer-badge"
                        disabled={isDisabled}
                        interactive={true}
                        style={{ overflowY: 'hidden', position: 'relative' }}
                        variant={chromeVariant}
                    >
                        <BadgeContent
                            defaultText={defaultText}
                            reviewer={item.reviewer}
                            showAnimatedIcon={showAnimatedIcon}
                        />
                    </Badge>
                </BadgeButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" collisionPadding={16} side="bottom" style={{ zIndex: 3000 }}>
                <GenericReviewerContextMenuComponent
                    item={item}
                    itemType={itemType}
                    jobArchitectureId={jobArchitectureId}
                    jobClusterId={jobClusterId}
                    onlyAssignReviewer={onlyAssignReviewer}
                    onInvite={onInvite}
                />
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const animations = {
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    initial: false,
    transition: { delay: 0.5, duration: 0.7 },
};

const shouldHideBadge = (showOnlyWhenUnassigned: boolean, item: JobItem): boolean => {
    return (showOnlyWhenUnassigned && item.reviewer !== undefined) || !item.actions?.canAssignReviewer;
};

export const GenericAssignReviewerBadge: FunctionComponent<{
    readonly badgeClassName?: string;
    readonly chromeVariant?: ReviewerBadgeChromeVariant;
    readonly disabled?: boolean;
    readonly item: JobItem;
    readonly itemType: 'job-level' | 'job-role';
    readonly jobArchitectureId: string;
    readonly jobClusterId?: string;
    readonly onInvite?: () => void;
    readonly onlyAssignReviewer?: boolean;
    readonly showOnlyWhenUnassigned?: boolean;
}> = ({
    badgeClassName,
    chromeVariant = 'warning',
    disabled = false,
    item,
    itemType,
    jobArchitectureId,
    jobClusterId,
    onInvite,
    onlyAssignReviewer = false,
    showOnlyWhenUnassigned = false,
}) => {
    const { translate } = useContext(ApplicationContext);
    const shouldHide = shouldHideBadge(showOnlyWhenUnassigned, item);

    const defaultText = translate('pages.job-architecture-v2.job-add-reviewer');

    const content = (
        <motion.div {...animations} onClick={(e) => e.stopPropagation()}>
            <ReviewerBadgeDropdown
                badgeClassName={badgeClassName}
                chromeVariant={chromeVariant}
                defaultText={defaultText}
                disabled={disabled}
                item={item}
                itemType={itemType}
                jobArchitectureId={jobArchitectureId}
                jobClusterId={jobClusterId}
                onlyAssignReviewer={onlyAssignReviewer}
                showAnimatedIcon={showOnlyWhenUnassigned}
                onInvite={onInvite}
            />
        </motion.div>
    );

    if (!item.actions?.canAssignReviewer && is.undefined(item.reviewer)) {
        return null;
    }

    if (showOnlyWhenUnassigned) {
        return <AnimatePresence mode="wait"> {shouldHide ? null : content}</AnimatePresence>;
    }

    return content;
};
